import { memo, useCallback, useEffect, useState } from 'react'
import type { AdSpot } from '../../store/slices/adsSlice.js'
import { spotsApi, type SpotMediaInfo, type SpotMediaReplacePreview } from '../../lib/adsApi.js'
import type { ProbeResult } from '../../lib/mediaBrowserApi.js'
import { formatDateShort, formatDuration, formatFileSize, formatTime } from '../../lib/utils.js'
import {
  spotMediaStatusLabel,
  spotMediaTone,
  spotReplaceBlockEffect,
  spotReplaceDecision,
  spotReplaceSummary,
} from '../../lib/spotMedia.js'
import { MediaBrowserModal } from '../programs/MediaBrowserModal.js'
import { Button } from '../ui/button.js'
import { Spinner } from '../common/Spinner.js'
// Même carte « Média associé » que la fiche programme : on reprend ses styles
// plutôt que d'en recopier une variante, comme le font déjà les jingles.
import '../programs/ProgramsView.css'

interface SpotMediaSectionProps {
  spot: AdSpot
  onReplaced: (spot: AdSpot) => void
  disabled?: boolean
  disabledReason?: string
}

interface PendingReplace {
  fileName: string
  data: { filePath: string; durationSec: number }
  preview: SpotMediaReplacePreview
}

function baseName(path: string): string {
  return path.split(/[\\/]/).pop() ?? path
}

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback
}

/**
 * Carte « Média associé » d'un spot, calquée sur celle d'un programme : fichier,
 * fiche technique, état, « Vérifier » et « Remplacer le fichier ».
 *
 * Le remplacement passe toujours par une prévisualisation serveur. Quand la
 * grille ne bouge pas, il s'applique aussitôt, comme pour un programme ; quand
 * des blocs à venir changent de longueur, ils sont listés avant validation.
 */
export const SpotMediaSection = memo(function SpotMediaSection({
  spot, onReplaced, disabled = false, disabledReason,
}: SpotMediaSectionProps) {
  const [info, setInfo] = useState<SpotMediaInfo | null>(null)
  const [checking, setChecking] = useState(false)
  const [showCheck, setShowCheck] = useState(false)
  const [browserOpen, setBrowserOpen] = useState(false)
  const [pending, setPending] = useState<PendingReplace | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  // Relu à chaque changement de fichier : la fiche technique est celle du
  // fichier présent sur le stockage, pas un instantané pris à l'association.
  useEffect(() => {
    let cancelled = false
    spotsApi.media(spot.id)
      .then(result => { if (!cancelled) setInfo(result) })
      .catch((cause: unknown) => {
        if (cancelled) return
        setInfo({
          status: 'ERROR', fileName: null, filePath: spot.filePath,
          codec: null, container: null, durationSec: null, resolution: null, fileSizeBytes: null,
          issues: [errorMessage(cause, 'Lecture du fichier impossible')],
        })
      })
    return () => { cancelled = true }
  }, [spot.id, spot.filePath])

  const handleCheck = useCallback(async () => {
    setChecking(true)
    try {
      setInfo(await spotsApi.media(spot.id))
    } catch (cause) {
      setInfo(previous => previous && { ...previous, status: 'ERROR', issues: [errorMessage(cause, 'Erreur de vérification')] })
    } finally {
      setShowCheck(true)
      setChecking(false)
    }
  }, [spot.id])

  const apply = useCallback(async (data: PendingReplace['data']) => {
    setBusy(true)
    setError('')
    try {
      const result = await spotsApi.applyReplaceMedia(spot.id, data)
      setPending(null)
      setShowCheck(false)
      onReplaced(result.spot as AdSpot)
    } catch (cause) {
      setError(errorMessage(cause, 'Impossible de remplacer le fichier'))
    } finally {
      setBusy(false)
    }
  }, [onReplaced, spot.id])

  const handleSelectFile = useCallback(async (result: ProbeResult) => {
    setBrowserOpen(false)
    setPending(null)
    setNotice('')
    // Même règle qu'à l'ajout d'un spot : la durée vient exclusivement du
    // fichier, jamais d'une saisie.
    if (result.durationSec == null || result.durationSec <= 0) {
      setError(`Durée non détectée pour « ${result.fileName} ». ${result.error ?? 'Vérifiez que le fichier n’est pas corrompu.'}`)
      return
    }
    const data = { filePath: result.filePath, durationSec: Math.round(result.durationSec) }
    setBusy(true)
    setError('')
    let preview: SpotMediaReplacePreview
    try {
      preview = await spotsApi.previewReplaceMedia(spot.id, data)
    } catch (cause) {
      setError(errorMessage(cause, 'Impossible de préparer le remplacement'))
      setBusy(false)
      return
    }
    setBusy(false)
    const decision = spotReplaceDecision(preview)
    // Ne pas « réussir » un remplacement qui ne change rien : c'est ce faux
    // succès qui laissait croire le fichier remplacé.
    if (decision === 'UNCHANGED') setNotice(`« ${result.fileName} » est déjà le fichier de ce spot : rien à remplacer.`)
    else if (decision === 'DIRECT') await apply(data)
    else setPending({ fileName: result.fileName, data, preview })
  }, [apply, spot.id])

  const tone = spotMediaTone(info)
  const durationSec = info?.durationSec ?? null

  return (
    <section className="catalog-media-section spot-media">
      <div className="catalog-media-section__header">
        <h3 className="catalog-media-section__title">Média associé</h3>
        <button
          type="button"
          onClick={() => { setError(''); setNotice(''); setPending(null); setBrowserOpen(true) }}
          className="catalog-media-associate"
          disabled={disabled || busy}
          title={disabled ? disabledReason : undefined}
        >
          {busy && !pending ? 'Analyse…' : spot.filePath ? 'Remplacer le fichier' : '+ Associer un fichier'}
        </button>
      </div>

      {disabled && disabledReason && (
        <div className="tp-feedback tp-feedback--warning catalog-media-section__feedback">
          {disabledReason}
        </div>
      )}

      {browserOpen && (
        <MediaBrowserModal
          onSelect={result => { void handleSelectFile(result) }}
          onClose={() => setBrowserOpen(false)}
          confirmLabel={spot.filePath ? 'Remplacer' : 'Associer'}
        />
      )}

      {error && (
        <div className="tp-feedback tp-feedback--error catalog-media-section__feedback" role="alert">
          {error}
        </div>
      )}

      {notice && (
        <div className="tp-feedback tp-feedback--success catalog-media-section__feedback" role="status">
          {notice}
        </div>
      )}

      {pending && (
        <ReplacePreviewPanel
          pending={pending}
          busy={busy}
          onConfirm={() => { void apply(pending.data) }}
          onCancel={() => setPending(null)}
        />
      )}

      <div className="catalog-media-card">
        {spot.filePath ? (
          <div className="catalog-media-row is-last">
            <div className="catalog-media-row__layout">
              <div className={`catalog-media-row__type catalog-media-row__type--${tone}`}>HD</div>
              <div className="catalog-media-row__copy">
                <div className="catalog-media-row__name">{info?.fileName ?? baseName(spot.filePath)}</div>
                <div className="catalog-media-row__meta">
                  {info?.codec && <span>{info.codec}</span>}
                  {info?.container && <span>· {info.container}</span>}
                  {durationSec != null && <span>· {formatDuration(Math.round(durationSec))}</span>}
                  {info?.resolution && <span>· {info.resolution}</span>}
                  {info?.fileSizeBytes && <span>· {formatFileSize(info.fileSizeBytes)}</span>}
                </div>
              </div>
              <span className={`catalog-status-badge catalog-status-badge--${tone}`}>
                {spotMediaStatusLabel(info)}
              </span>
              <div className="catalog-media-row__actions">
                <button
                  type="button"
                  onClick={() => { void handleCheck() }}
                  disabled={checking || busy}
                  className="catalog-mini-button"
                >
                  {checking ? '…' : 'Vérifier'}
                </button>
              </div>
            </div>

            {/* Après « Vérifier », le résultat complet ; sinon le premier écart
                connu, comme l'erreur précédente d'un média de programme. */}
            {info && showCheck && (
              <div className={`catalog-media-row__result is-${tone === 'muted' ? 'warning' : tone}`}>
                {info.issues.length === 0
                  ? '✓ Fichier présent et lisible — durée conforme au spot'
                  : info.issues.map((issue, index) => <div key={index}>⚠ {issue}</div>)}
              </div>
            )}
            {info && !showCheck && info.issues.length > 0 && (
              <div className={`catalog-media-row__result is-${tone === 'muted' ? 'warning' : tone} is-compact`}>
                {info.issues[0]}
              </div>
            )}
          </div>
        ) : (
          <div className="catalog-media-card__empty">Aucun fichier associé à ce spot</div>
        )}
      </div>
    </section>
  )
})

// ─── Prévisualisation du remplacement ─────────────────────────────────────────

/**
 * Le recalage est la règle qui fonctionne comme prévu, pas un défaut : ton
 * informatif et verbe « Remplacer ». Seul le blocage prend le ton d'alerte.
 */
const ReplacePreviewPanel = memo(function ReplacePreviewPanel({
  pending, busy, onConfirm, onCancel,
}: {
  pending: PendingReplace
  busy: boolean
  onConfirm: () => void
  onCancel: () => void
}) {
  const { preview } = pending
  const blocked = spotReplaceDecision(preview) === 'BLOCKED'
  const resized = preview.blocks.filter(block => block.side !== null)

  return (
    <div className={`spot-media-replace${blocked ? ' is-blocked' : ''}`} role={blocked ? 'alert' : 'status'}>
      <div className="spot-media-replace__file">
        Nouveau fichier : <strong>{pending.fileName}</strong>
      </div>

      {blocked ? (
        <>
          <p className="spot-media-replace__lead">
            Remplacement impossible : {preview.blockers.length > 1
              ? `${preview.blockers.length} blocs à venir ne peuvent pas être recalés.`
              : 'un bloc à venir ne peut pas être recalé.'}
          </p>
          <ul className="spot-media-replace__list">
            {preview.blockers.map(blocker => (
              <li key={blocker.itemId}>
                <span className="spot-media-replace__when">{formatDateShort(blocker.startTime)} {formatTime(blocker.startTime)}</span>
                {blocker.label}
              </li>
            ))}
          </ul>
          <div className="spot-media-replace__actions">
            <Button type="button" variant="outline" size="sm" onClick={onCancel}>Fermer</Button>
          </div>
        </>
      ) : (
        <>
          <p className="spot-media-replace__lead">{spotReplaceSummary(preview)}</p>
          <ul className="spot-media-replace__list">
            {resized.map(block => (
              <li key={block.itemId}>
                <span className="spot-media-replace__when">{formatDateShort(block.startTime)}</span>
                <span className="spot-media-replace__times">
                  {formatTime(block.startTime)}–{formatTime(block.endTime)} → {formatTime(block.newStartTime)}–{formatTime(block.newEndTime)}
                </span>
                <span className="spot-media-replace__effect">{spotReplaceBlockEffect(block)}</span>
              </li>
            ))}
          </ul>
          <p className="spot-media-replace__note">Les autres éléments de la grille gardent leur horaire.</p>
          <div className="spot-media-replace__actions">
            <Button type="button" size="sm" onClick={onConfirm} disabled={busy}>
              {busy && <Spinner />}
              Remplacer
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={onCancel} disabled={busy}>Annuler</Button>
          </div>
        </>
      )}
    </div>
  )
})
